import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, HttpResponse } from '@wix/sdk-types';
import * as ambassadorWixBillingV1TaxGroup from './billing-v1-tax-group-tax-groups.http.js';
// @ts-ignore

/**
 * A tax group is a category of specific line items grouped together based on their tax treatment.
 * You can create new tax groups to apply distinct tax rates and rules.
 */
export interface TaxGroup {
  /**
   * Tax group ID.
   * @readonly
   */
  _id?: string | null;
  /** Tax group name. */
  name?: string;
  /**
   * Revision number, which increments by 1 each time the tax group is updated. To prevent conflicting changes,
   * the current revision must be passed when updating the tax group.
   *
   * Ignored when creating a tax group.
   */
  revision?: string | null;
  /**
   * Date and time the tax group was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the tax group was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
}

export interface CreateTaxGroupRequest {
  /** Tax group to create. */
  taxGroup: TaxGroup;
}

export interface CreateTaxGroupResponse {
  /** Created tax group. */
  taxGroup?: TaxGroup;
}

export interface GetTaxGroupRequest {
  /** ID of the tax group to retrieve. */
  taxGroupId: string;
}

export interface GetTaxGroupResponse {
  /** Retrieved tax group. */
  taxGroup?: TaxGroup;
}

export interface UpdateTaxGroupRequest {
  /** Tax group info to update. */
  taxGroup: TaxGroup;
}

export interface UpdateTaxGroupResponse {
  /** Updated tax group. */
  taxGroup?: TaxGroup;
}

export interface DeleteTaxGroupRequest {
  /** ID of the tax group to delete. */
  taxGroupId: string;
}

export interface DeleteTaxGroupResponse {}

export interface QueryTaxGroupsRequest {
  /** Query options. */
  query: QueryV2;
}

export interface QueryV2 extends QueryV2PagingMethodOneOf {
  /** Paging options to limit and skip the number of items. */
  paging?: Paging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about the [filter section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-filter-section).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about the [sort section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-sort-section).
   */
  sort?: Sorting[];
  /** Array of projected fields. A list of specific field names to return. If `fieldsets` are also specified, the union of `fieldsets` and `fields` is returned. */
  fields?: string[];
  /** Array of named, predefined sets of projected fields. A array of predefined named sets of fields to be returned. Specifying multiple `fieldsets` will return the union of fields from all sets. If `fields` are also specified, the union of `fieldsets` and `fields` is returned. */
  fieldsets?: string[];
}

/** @oneof */
export interface QueryV2PagingMethodOneOf {
  /** Paging options to limit and skip the number of items. */
  paging?: Paging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /** Name of the field to sort by. */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrder;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

export interface Paging {
  /** Number of items to load. */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface CursorPaging {
  /** Maximum number of items to return in the results. */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   */
  cursor?: string | null;
}

export interface QueryTaxGroupsResponse {
  /** Retrieved default tax groups. */
  taxGroups?: TaxGroup[];
  /** Paging metadata. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in current page. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /** Cursor string pointing to the next page in the list of results. */
  next?: string | null;
  /** Cursor pointing to the previous page in the list of results. */
  prev?: string | null;
}

export interface ListDefaultTaxGroupsRequest {}

export interface ListDefaultTaxGroupsResponse {
  /** Retrieved default tax groups. */
  taxGroups?: TaxGroup[];
}

export interface ListDefaultTaxGroupsByAppIdsRequest {
  /** App IDs to retrieve default tax groups for. */
  appIds: string[];
}

export interface ListDefaultTaxGroupsByAppIdsResponse {
  /** Retrieved default tax groups. */
  results?: ListDefaultTaxGroupsByAppIdsResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface ListDefaultTaxGroupsByAppIdsResult {
  /** Information about success or failure to retrieve default tax groups. */
  taxGroupMetadata?: ItemMetadata;
  /** Retrieved default tax groups. */
  taxGroup?: TaxGroup;
}

export interface ItemMetadata {
  /** Item ID. Should always be available, unless it's impossible (for example, when failing to create an item). */
  _id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /**
   * Unique event ID.
   * Allows clients to ignore duplicate webhooks.
   */
  _id?: string;
  /**
   * Assumes actions are also always typed to an entity_type
   * Example: wix.stores.catalog.product, wix.bookings.session, wix.payments.transaction
   */
  entityFqdn?: string;
  /**
   * This is top level to ease client code dispatching of messages (switch on entity_fqdn+slug)
   * This is although the created/updated/deleted notion is duplication of the oneof types
   * Example: created/updated/deleted/started/completed/email_opened
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example: 2020-04-26T13:57:50.699Z */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number defining the order of updates to the underlying entity.
   * For example, given that some entity was updated at 16:00 and than again at 16:01,
   * it is guaranteed that the sequence number of the second update is strictly higher than the first.
   * As the consumer, you can use this value to ensure that you handle messages in the correct order.
   * To do so, you will need to persist this number on your end, and compare the sequence number from the
   * message against the one you have stored. Given that the stored number is higher, you should ignore the message.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /** App instance ID. */
  instanceId?: string | null;
  /** Event type. */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /** ID of a site visitor that has not logged in to the site. */
  anonymousVisitorId?: string;
  /** ID of a site visitor that has logged in to the site. */
  memberId?: string;
  /** ID of a Wix user (site owner, contributor, etc.). */
  wixUserId?: string;
  /** ID of an app. */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityType;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /** ID of a site visitor that has not logged in to the site. */
  anonymousVisitorId?: string;
  /** ID of a site visitor that has logged in to the site. */
  memberId?: string;
  /** ID of a Wix user (site owner, contributor, etc.). */
  wixUserId?: string;
  /** ID of an app. */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

export interface TaxGroupNonNullableFields {
  name: string;
}

export interface CreateTaxGroupResponseNonNullableFields {
  taxGroup?: TaxGroupNonNullableFields;
}

export interface GetTaxGroupResponseNonNullableFields {
  taxGroup?: TaxGroupNonNullableFields;
}

export interface UpdateTaxGroupResponseNonNullableFields {
  taxGroup?: TaxGroupNonNullableFields;
}

export interface QueryTaxGroupsResponseNonNullableFields {
  taxGroups: TaxGroupNonNullableFields[];
}

export interface ListDefaultTaxGroupsResponseNonNullableFields {
  taxGroups: TaxGroupNonNullableFields[];
}

interface ApplicationErrorNonNullableFields {
  code: string;
  description: string;
}

interface ItemMetadataNonNullableFields {
  originalIndex: number;
  success: boolean;
  error?: ApplicationErrorNonNullableFields;
}

interface ListDefaultTaxGroupsByAppIdsResultNonNullableFields {
  taxGroupMetadata?: ItemMetadataNonNullableFields;
  taxGroup?: TaxGroupNonNullableFields;
}

interface BulkActionMetadataNonNullableFields {
  totalSuccesses: number;
  totalFailures: number;
  undetailedFailures: number;
}

export interface ListDefaultTaxGroupsByAppIdsResponseNonNullableFields {
  results: ListDefaultTaxGroupsByAppIdsResultNonNullableFields[];
  bulkActionMetadata?: BulkActionMetadataNonNullableFields;
}

export interface BaseEventMetadata {
  /** App instance ID. */
  instanceId?: string | null;
  /** Event type. */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
}

export interface EventMetadata extends BaseEventMetadata {
  /**
   * Unique event ID.
   * Allows clients to ignore duplicate webhooks.
   */
  _id?: string;
  /**
   * Assumes actions are also always typed to an entity_type
   * Example: wix.stores.catalog.product, wix.bookings.session, wix.payments.transaction
   */
  entityFqdn?: string;
  /**
   * This is top level to ease client code dispatching of messages (switch on entity_fqdn+slug)
   * This is although the created/updated/deleted notion is duplication of the oneof types
   * Example: created/updated/deleted/started/completed/email_opened
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example: 2020-04-26T13:57:50.699Z */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number defining the order of updates to the underlying entity.
   * For example, given that some entity was updated at 16:00 and than again at 16:01,
   * it is guaranteed that the sequence number of the second update is strictly higher than the first.
   * As the consumer, you can use this value to ensure that you handle messages in the correct order.
   * To do so, you will need to persist this number on your end, and compare the sequence number from the
   * message against the one you have stored. Given that the stored number is higher, you should ignore the message.
   */
  entityEventSequence?: string | null;
}

export interface TaxGroupCreatedEnvelope {
  entity: TaxGroup;
  metadata: EventMetadata;
}

/**
 * Triggered when a tax group is created.
 * @permissionScope Manage Stores - all permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.MANAGE-STORES
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Manage eCommerce - all permissions
 * @permissionScopeId SCOPE.DC-ECOM-MEGA.MANAGE-ECOM
 * @permissionScope Manage Orders
 * @permissionScopeId SCOPE.DC-STORES.MANAGE-ORDERS
 * @permissionId BILLING.TAX_GROUP_READ
 * @webhook
 * @eventType wix.billing.v1.tax_group_created
 * @documentationMaturity preview
 */
export declare function onTaxGroupCreated(
  handler: (event: TaxGroupCreatedEnvelope) => void | Promise<void>
): void;

export interface TaxGroupDeletedEnvelope {
  metadata: EventMetadata;
}

/**
 * Triggered when a tax group is deleted.
 * @permissionScope Manage Stores - all permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.MANAGE-STORES
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Manage eCommerce - all permissions
 * @permissionScopeId SCOPE.DC-ECOM-MEGA.MANAGE-ECOM
 * @permissionScope Manage Orders
 * @permissionScopeId SCOPE.DC-STORES.MANAGE-ORDERS
 * @permissionId BILLING.TAX_GROUP_READ
 * @webhook
 * @eventType wix.billing.v1.tax_group_deleted
 * @documentationMaturity preview
 */
export declare function onTaxGroupDeleted(
  handler: (event: TaxGroupDeletedEnvelope) => void | Promise<void>
): void;

export interface TaxGroupUpdatedEnvelope {
  entity: TaxGroup;
  metadata: EventMetadata;
}

/**
 * Triggered when a tax group is updated.
 * @permissionScope Manage Stores - all permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.MANAGE-STORES
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Manage eCommerce - all permissions
 * @permissionScopeId SCOPE.DC-ECOM-MEGA.MANAGE-ECOM
 * @permissionScope Manage Orders
 * @permissionScopeId SCOPE.DC-STORES.MANAGE-ORDERS
 * @permissionId BILLING.TAX_GROUP_READ
 * @webhook
 * @eventType wix.billing.v1.tax_group_updated
 * @documentationMaturity preview
 */
export declare function onTaxGroupUpdated(
  handler: (event: TaxGroupUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * Creates a tax group.
 *
 * Call Stores Update Product to add the `taxGroupId` to specific products to categorize as a group based on distinct tax treatment.
 * Wix uses tax groups to calculate tax.
 *
 * In addition to tax groups you create, default tax groups are already included in all Wix catalogs.
 * Call List Default Tax Groups to retrieve them. You can also use the Tax Groups Integration service plugin (REST only)
 * to create new default tax groups that can be applied directly to an entire catalog of products.
 * @param taxGroup - Tax group to create.
 * @public
 * @documentationMaturity preview
 * @requiredField taxGroup
 * @requiredField taxGroup.name
 * @permissionId BILLING.TAX_GROUP_CREATE
 * @permissionScope Manage Stores - all permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.MANAGE-STORES
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Manage Orders
 * @permissionScopeId SCOPE.DC-STORES.MANAGE-ORDERS
 * @applicableIdentity APP
 * @returns Created tax group.
 * @fqn wix.billing.v1.TaxGroups.CreateTaxGroup
 */
export async function createTaxGroup(
  taxGroup: TaxGroup
): Promise<TaxGroup & TaxGroupNonNullableFields> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ taxGroup: taxGroup });

  const reqOpts = ambassadorWixBillingV1TaxGroup.createTaxGroup(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.taxGroup!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { taxGroup: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['taxGroup']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a tax group.
 * @param taxGroupId - ID of the tax group to retrieve.
 * @public
 * @documentationMaturity preview
 * @requiredField taxGroupId
 * @permissionId BILLING.TAX_GROUP_READ
 * @permissionScope Manage Stores - all permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.MANAGE-STORES
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Manage eCommerce - all permissions
 * @permissionScopeId SCOPE.DC-ECOM-MEGA.MANAGE-ECOM
 * @permissionScope Manage Orders
 * @permissionScopeId SCOPE.DC-STORES.MANAGE-ORDERS
 * @applicableIdentity APP
 * @applicableIdentity VISITOR
 * @returns Retrieved tax group.
 * @fqn wix.billing.v1.TaxGroups.GetTaxGroup
 */
export async function getTaxGroup(
  taxGroupId: string
): Promise<TaxGroup & TaxGroupNonNullableFields> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    taxGroupId: taxGroupId,
  });

  const reqOpts = ambassadorWixBillingV1TaxGroup.getTaxGroup(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.taxGroup!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { taxGroupId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['taxGroupId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Updates a tax group.
 *
 * Each time the tax group is updated, `revision` increments by 1.
 * The current `revision` must be passed when updating the tax group.
 * This ensures you're working with the latest tax group and prevents
 * unintended overwrites.
 * @param _id - Tax group ID.
 * @public
 * @documentationMaturity preview
 * @requiredField _id
 * @requiredField taxGroup
 * @requiredField taxGroup.name
 * @requiredField taxGroup.revision
 * @param taxGroup - Tax group info.
 * @permissionId BILLING.TAX_GROUP_UPDATE
 * @permissionScope Manage Stores - all permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.MANAGE-STORES
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Manage Orders
 * @permissionScopeId SCOPE.DC-STORES.MANAGE-ORDERS
 * @applicableIdentity APP
 * @returns Updated tax group.
 * @fqn wix.billing.v1.TaxGroups.UpdateTaxGroup
 */
export async function updateTaxGroup(
  _id: string | null,
  taxGroup: UpdateTaxGroup
): Promise<TaxGroup & TaxGroupNonNullableFields> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    taxGroup: { ...taxGroup, id: _id },
  });

  const reqOpts = ambassadorWixBillingV1TaxGroup.updateTaxGroup(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.taxGroup!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { taxGroup: '$[1]' },
        explicitPathsToArguments: { 'taxGroup.id': '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'taxGroup']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateTaxGroup {
  /**
   * Tax group ID.
   * @readonly
   */
  _id?: string | null;
  /** Tax group name. */
  name?: string;
  /**
   * Revision number, which increments by 1 each time the tax group is updated. To prevent conflicting changes,
   * the current revision must be passed when updating the tax group.
   *
   * Ignored when creating a tax group.
   */
  revision?: string | null;
  /**
   * Date and time the tax group was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the tax group was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
}

/**
 * Deletes a tax group.
 *
 * If a tax group is deleted but the `taxGroupId` is still assigned to a product (see Stores Products API) then the default tax group is used to calculate tax.
 * @param taxGroupId - ID of the tax group to delete.
 * @public
 * @documentationMaturity preview
 * @requiredField taxGroupId
 * @permissionId BILLING.TAX_GROUP_DELETE
 * @permissionScope Manage Stores - all permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.MANAGE-STORES
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Manage Orders
 * @permissionScopeId SCOPE.DC-STORES.MANAGE-ORDERS
 * @applicableIdentity APP
 * @fqn wix.billing.v1.TaxGroups.DeleteTaxGroup
 */
export async function deleteTaxGroup(taxGroupId: string): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    taxGroupId: taxGroupId,
  });

  const reqOpts = ambassadorWixBillingV1TaxGroup.deleteTaxGroup(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { taxGroupId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['taxGroupId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Creates a query to retrieve a list of tax groups.
 *
 * The `queryTaxGroups()` function builds a query to retrieve a list of tax groups and returns a `TaxGroupsQueryBuilder` object.
 *
 * The returned object contains the query definition, which is typically used to run the query using the `find()` function.
 *
 * You can refine the query by chaining `TaxGroupsQueryBuilder` functions onto the query. `TaxGroupsQueryBuilder` functions enable you to sort, filter, and control the results that `queryTaxGroups()` returns.
 *
 * `queryTaxGroups()` runs with the following `TaxGroupsQueryBuilder` default that you can override:
 * + `ascending("_id")`
 *
 * The functions that are chained to `queryTaxGroups()` are applied in the order they are called. For example, if you apply `ascending("name")` and then `ascending("_createdDate")`, the results are sorted first by the `"name"`, and then, if there are multiple results with the same `"name"`, the items are sorted by `"_createdDate"`.
 *
 * The following `TaxGroupsQueryBuilder` functions are supported for the `queryTaxGroups()` function. For a full description of the tax group object, see the object returned for the `items` property in `TaxGroupsQueryResult`.
 * @public
 * @documentationMaturity preview
 * @permissionScope Manage Stores - all permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.MANAGE-STORES
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Manage eCommerce - all permissions
 * @permissionScopeId SCOPE.DC-ECOM-MEGA.MANAGE-ECOM
 * @permissionScope Manage Orders
 * @permissionScopeId SCOPE.DC-STORES.MANAGE-ORDERS
 * @permissionId BILLING.TAX_GROUP_READ
 * @applicableIdentity APP
 * @applicableIdentity VISITOR
 * @fqn wix.billing.v1.TaxGroups.QueryTaxGroups
 */
export function queryTaxGroups(): TaxGroupsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    TaxGroup,
    'CURSOR',
    QueryTaxGroupsRequest,
    QueryTaxGroupsResponse
  >({
    func: async (payload: QueryTaxGroupsRequest) => {
      const reqOpts = ambassadorWixBillingV1TaxGroup.queryTaxGroups(payload);

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryTaxGroupsRequest['query']) => {
      const args = [query, {}] as [QueryTaxGroupsRequest['query'], {}];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({ data }: HttpResponse<QueryTaxGroupsResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(data);

      return {
        items: transformedData?.taxGroups,
        pagingMetadata: transformedData?.pagingMetadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface TaxGroupsQueryResult extends QueryCursorResult {
  items: TaxGroup[];
  query: TaxGroupsQueryBuilder;
  next: () => Promise<TaxGroupsQueryResult>;
  prev: () => Promise<TaxGroupsQueryResult>;
}

export interface TaxGroupsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  eq: (
    propertyName: '_id' | 'name' | '_createdDate' | '_updatedDate',
    value: any
  ) => TaxGroupsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ne: (
    propertyName: '_id' | 'name' | '_createdDate' | '_updatedDate',
    value: any
  ) => TaxGroupsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ge: (
    propertyName: '_createdDate' | '_updatedDate',
    value: any
  ) => TaxGroupsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  gt: (
    propertyName: '_createdDate' | '_updatedDate',
    value: any
  ) => TaxGroupsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  le: (
    propertyName: '_createdDate' | '_updatedDate',
    value: any
  ) => TaxGroupsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  lt: (
    propertyName: '_createdDate' | '_updatedDate',
    value: any
  ) => TaxGroupsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   * @documentationMaturity preview
   */
  hasSome: (
    propertyName: '_createdDate' | '_updatedDate',
    value: any[]
  ) => TaxGroupsQueryBuilder;
  /** @documentationMaturity preview */
  in: (
    propertyName: '_id' | 'name' | '_createdDate' | '_updatedDate',
    value: any
  ) => TaxGroupsQueryBuilder;
  /** @documentationMaturity preview */
  exists: (
    propertyName: '_createdDate' | '_updatedDate',
    value: boolean
  ) => TaxGroupsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments.
   * @documentationMaturity preview
   */
  ascending: (
    ...propertyNames: Array<'_id' | 'name' | '_createdDate' | '_updatedDate'>
  ) => TaxGroupsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments.
   * @documentationMaturity preview
   */
  descending: (
    ...propertyNames: Array<'_id' | 'name' | '_createdDate' | '_updatedDate'>
  ) => TaxGroupsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object.
   * @documentationMaturity preview
   */
  limit: (limit: number) => TaxGroupsQueryBuilder;
  /** @param cursor - A pointer to specific record
   * @documentationMaturity preview
   */
  skipTo: (cursor: string) => TaxGroupsQueryBuilder;
  /** @documentationMaturity preview */
  find: () => Promise<TaxGroupsQueryResult>;
}

/**
 * Retrieves a list of default tax groups.
 *
 * The default tax groups for a site are inherited by the apps installed on the site.
 * For example, the Wix Stores app includes a `"Products"` tax group by default.
 *
 * Add additional default tax groups with the Tax Groups Integration service plugin (REST only).
 * @public
 * @documentationMaturity preview
 * @permissionId BILLING.TAX_GROUP_READ
 * @permissionScope Manage Stores - all permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.MANAGE-STORES
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Manage eCommerce - all permissions
 * @permissionScopeId SCOPE.DC-ECOM-MEGA.MANAGE-ECOM
 * @permissionScope Manage Orders
 * @permissionScopeId SCOPE.DC-STORES.MANAGE-ORDERS
 * @applicableIdentity APP
 * @applicableIdentity VISITOR
 * @fqn wix.billing.v1.TaxGroups.ListDefaultTaxGroups
 */
export async function listDefaultTaxGroups(): Promise<
  ListDefaultTaxGroupsResponse & ListDefaultTaxGroupsResponseNonNullableFields
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({});

  const reqOpts = ambassadorWixBillingV1TaxGroup.listDefaultTaxGroups(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {},
        singleArgumentUnchanged: false,
      },
      []
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves default tax groups for specific apps.
 * @param appIds - App IDs to retrieve default tax groups for.
 * @public
 * @documentationMaturity preview
 * @requiredField appIds
 * @permissionId BILLING.TAX_GROUP_READ
 * @permissionScope Manage Stores - all permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.MANAGE-STORES
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Manage eCommerce - all permissions
 * @permissionScopeId SCOPE.DC-ECOM-MEGA.MANAGE-ECOM
 * @permissionScope Manage Orders
 * @permissionScopeId SCOPE.DC-STORES.MANAGE-ORDERS
 * @applicableIdentity APP
 * @applicableIdentity VISITOR
 * @fqn wix.billing.v1.TaxGroups.ListDefaultTaxGroupsByAppIds
 */
export async function listDefaultTaxGroupsByAppIds(
  appIds: string[]
): Promise<
  ListDefaultTaxGroupsByAppIdsResponse &
    ListDefaultTaxGroupsByAppIdsResponseNonNullableFields
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ appIds: appIds });

  const reqOpts =
    ambassadorWixBillingV1TaxGroup.listDefaultTaxGroupsByAppIds(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { appIds: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['appIds']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}
